import {AppointmentHour} from 'domain/usecases';
import {ParseAppointmentHour} from './types';
import {parseWeekDay, WEEK_DAYS} from './parseWeekDay';

const appointmentClosed = (weekDay: string): AppointmentHour => ({
	nmDiaSemana: weekDay,
	hrInicialPrimeiroTurno: null,
	hrFinalPrimeiroTurno: null,
	hrInicialSegundoTurno: null,
	hrFinalSegundoTurno: null,
});

const weekDaysOrder = [
	WEEK_DAYS.MONDAY,
	WEEK_DAYS.TUESDAY,
	WEEK_DAYS.WEDNESDAY,
	WEEK_DAYS.THURSDAY,
	WEEK_DAYS.FRIDAY,
	WEEK_DAYS.SATURDAY,
	WEEK_DAYS.SUNDAY,
];

const fillWeekDays = (appointmentHours: AppointmentHour[]): AppointmentHour[] =>
	weekDaysOrder.map(weekDay => {
		const appointmentHour = appointmentHours.find(hour => hour.nmDiaSemana === weekDay);
		return appointmentHour || appointmentClosed(weekDay);
	});

const removeTimeSeconds = (time: string) => time.slice(0, -3);
const formatTimeShift = (start: string, end: string) =>
	`${removeTimeSeconds(start)} às ${removeTimeSeconds(end)}`;

const parseAppointmentHour = (appointmentHour: AppointmentHour): ParseAppointmentHour => {
	const firstTimeShift =
		appointmentHour.hrInicialPrimeiroTurno && appointmentHour.hrFinalPrimeiroTurno
			? formatTimeShift(
					appointmentHour.hrInicialPrimeiroTurno,
					appointmentHour.hrFinalPrimeiroTurno,
				)
			: null;

	const secondTimeShift =
		appointmentHour.hrInicialSegundoTurno && appointmentHour.hrFinalSegundoTurno
			? formatTimeShift(
					appointmentHour.hrInicialSegundoTurno,
					appointmentHour.hrFinalSegundoTurno,
				)
			: null;

	const dayOfWeek = new Date().getDay();
	const dayOfWeekParsed = dayOfWeek === 0 ? 7 : dayOfWeek;
	const isCurrentDay = weekDaysOrder.indexOf(appointmentHour.nmDiaSemana) + 1 === dayOfWeekParsed;

	return {
		weekDay: parseWeekDay(appointmentHour.nmDiaSemana),
		firstShift: firstTimeShift,
		secondShift: secondTimeShift,
		isClosed: !firstTimeShift && !secondTimeShift,
		isCurrentDay,
	};
};

const parseAppointmentHours = (
	appointmentHours: AppointmentHour[] | null,
): ParseAppointmentHour[] => {
	if (!appointmentHours || appointmentHours.length === 0) return [];

	const filledWeekDays = fillWeekDays(appointmentHours);
	return filledWeekDays.map(parseAppointmentHour);
};

function currentDayFirstWhenNotExpanded({
	dateTimes,
	isExpanded,
}: {
	dateTimes: ParseAppointmentHour[];
	isExpanded: boolean;
}) {
	if (isExpanded) return dateTimes;
	const currentIdx = dateTimes.findIndex(day => day.isCurrentDay);
	if (currentIdx > -1) {
		const reordered = [...dateTimes];
		const [currentDay] = reordered.splice(currentIdx, 1);
		reordered.unshift(currentDay);
		return reordered;
	}
	return dateTimes;
}

export {parseAppointmentHours, currentDayFirstWhenNotExpanded};
